import React, { useEffect, useState, useCallback } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ShieldAlert, Eye, Clock, CheckCircle2, XCircle, Loader2, FileWarning } from 'lucide-react';
import { toast } from 'sonner';
import { PiiConsistencyDialog } from '@/components/semantic-models/PiiConsistencyDialog';

const ADMIN_ROLES = ['admin', 'super_admin', 'tenant_admin', 'owner'];

function fmtTs(ts) {
  if (!ts) return '—';
  try { return new Date(ts).toLocaleString(); } catch { return ts; }
}

function StatCard({ label, value, icon: Icon }) {
  return (
    <div className="glass-card rounded-2xl p-5 flex items-center gap-4" data-testid={`pii-stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center"><Icon className="w-5 h-5 text-primary" /></div>
      <div>
        <div className="text-2xl font-semibold text-foreground">{value ?? 0}</div>
        <div className="text-xs text-muted-foreground uppercase tracking-wider">{label}</div>
      </div>
    </div>
  );
}

function AccessReport() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.getPiiAccessReport(days).then(setData).catch(e => toast.error(e.message)).finally(() => setLoading(false));
  }, [days]);
  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  if (!data) return null;

  return (
    <div className="space-y-6" data-testid="pii-access-report">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">Who accessed PII fields and when.</p>
        <Select value={String(days)} onValueChange={v => setDays(Number(v))}>
          <SelectTrigger className="w-40" data-testid="pii-days-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
            <SelectItem value="365">Last year</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Total Accesses" value={data.total_accesses} icon={Eye} />
        <StatCard label="PII Fields" value={data.distinct_fields} icon={ShieldAlert} />
        <StatCard label="Users" value={data.distinct_users} icon={Eye} />
        <StatCard label="Window (days)" value={data.days} icon={Clock} />
      </div>

      {data.total_accesses === 0 ? (
        <div className="glass-card rounded-2xl p-10 text-center text-muted-foreground">
          <FileWarning className="w-10 h-10 mx-auto mb-3 opacity-40" />
          No PII field access recorded in this window.
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-6">
          <div className="glass-card rounded-2xl p-5">
            <h3 className="text-sm font-semibold mb-3">By Field</h3>
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs text-muted-foreground border-b border-border/40"><th className="py-2">Field</th><th>Accesses</th><th>Users</th><th>Last</th></tr></thead>
              <tbody>
                {data.by_field.map(f => (
                  <tr key={f.field} className="border-b border-border/20" data-testid={`pii-field-row-${f.field}`}>
                    <td className="py-2"><Badge variant="outline" className="text-rose-400 border-rose-500/30">{f.field}</Badge></td>
                    <td className="text-foreground">{f.access_count}</td><td className="text-foreground">{f.distinct_users}</td><td className="text-xs text-muted-foreground">{fmtTs(f.last_access)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="glass-card rounded-2xl p-5">
            <h3 className="text-sm font-semibold mb-3">By User</h3>
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs text-muted-foreground border-b border-border/40"><th className="py-2">User</th><th>Accesses</th><th>Fields</th><th>Last</th></tr></thead>
              <tbody>
                {data.by_user.map(u => (
                  <tr key={u.user_id} className="border-b border-border/20">
                    <td className="py-2">{u.user_name}</td><td className="text-foreground">{u.access_count}</td><td className="text-foreground">{u.field_count}</td><td className="text-xs text-muted-foreground">{fmtTs(u.last_access)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function Approvals() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modelNames, setModelNames] = useState({});

  const load = useCallback(() => {
    setLoading(true);
    api.listUnmaskRequests().then(r => setRows(r || [])).catch(e => toast.error(e.message)).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.getSemanticModels().then(m => {
      const map = {};
      (m || []).forEach(x => { map[x.model_id || x.id] = x.name || x.display_name; });
      setModelNames(map);
    }).catch(() => {});
  }, []);

  const act = async (id, kind) => {
    try {
      if (kind === 'approve') await api.approveUnmaskRequest(id); else await api.denyUnmaskRequest(id);
      toast.success(kind === 'approve' ? 'Approved (2-hour grant)' : 'Denied');
      load();
    } catch (e) { toast.error(e.message); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  const badge = (s) => {
    const map = { pending: 'text-amber-400 border-amber-500/30', approved: 'text-emerald-400 border-emerald-500/30', denied: 'text-rose-400 border-rose-500/30' };
    return <Badge variant="outline" className={map[s] || ''}>{s}</Badge>;
  };

  return (
    <div className="space-y-4" data-testid="pii-approvals">
      {rows.length === 0 ? (
        <div className="glass-card rounded-2xl p-10 text-center text-muted-foreground">No unmask requests yet.</div>
      ) : rows.map(r => (
        <div key={r.request_id} className="glass-card rounded-2xl p-5 flex items-start justify-between gap-4" data-testid={`unmask-req-${r.request_id}`}>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className="font-medium">{r.user_name}</span> {badge(r.status)}
              {r.status === 'approved' && r.expires_at && <span className="text-xs text-muted-foreground">expires {fmtTs(r.expires_at)}</span>}
            </div>
            <div className="text-sm text-muted-foreground">Model: <span className="text-foreground">{modelNames[r.model_id] || r.model_id}</span></div>
            <div className="flex flex-wrap gap-1 mt-1">{(r.fields || []).map(f => <Badge key={f} variant="outline" className="text-rose-400 border-rose-500/30 text-[10px]">{f}</Badge>)}</div>
            {r.reason && <div className="text-xs text-muted-foreground mt-1 italic">“{r.reason}”</div>}
            <div className="text-[10px] text-muted-foreground mt-1">Requested {fmtTs(r.created_at)}</div>
          </div>
          {r.status === 'pending' && (
            <div className="flex gap-2">
              <Button size="sm" onClick={() => act(r.request_id, 'approve')} className="gap-1" data-testid={`unmask-approve-${r.request_id}`}><CheckCircle2 className="w-4 h-4" /> Approve</Button>
              <Button size="sm" variant="outline" onClick={() => act(r.request_id, 'deny')} className="gap-1" data-testid={`unmask-deny-${r.request_id}`}><XCircle className="w-4 h-4" /> Deny</Button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function RequestAccess() {
  const [models, setModels] = useState([]);
  const [modelId, setModelId] = useState('');
  const [piiFields, setPiiFields] = useState([]);
  const [selected, setSelected] = useState([]);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [mine, setMine] = useState([]);

  useEffect(() => { api.getSemanticModels().then(m => setModels(m || [])).catch(() => {}); loadMine(); }, []);
  const loadMine = () => api.listUnmaskRequests().then(r => setMine(r || [])).catch(() => {});

  useEffect(() => {
    if (!modelId) { setPiiFields([]); setSelected([]); return; }
    api.getSemanticModelFull(modelId).then(m => {
      const fields = [];
      (m.entities || []).forEach(e => (e.attributes || []).forEach(a => { if (a.pii && !fields.includes(a.name)) fields.push(a.name); }));
      setPiiFields(fields); setSelected([]);
    }).catch(() => setPiiFields([]));
  }, [modelId]);

  const toggle = (f) => setSelected(p => p.includes(f) ? p.filter(x => x !== f) : [...p, f]);

  const submit = async () => {
    if (!modelId || selected.length === 0) { toast.error('Pick a model and at least one field'); return; }
    setSubmitting(true);
    try {
      await api.createUnmaskRequest({ model_id: modelId, fields: selected, reason });
      toast.success('Request submitted for admin approval');
      setSelected([]); setReason(''); loadMine();
    } catch (e) { toast.error(e.message); } finally { setSubmitting(false); }
  };

  return (
    <div className="space-y-6" data-testid="pii-request-access">
      <div className="glass-card rounded-2xl p-6 max-w-2xl space-y-4">
        <p className="text-sm text-muted-foreground">Request temporary (2-hour) unmasked access to PII fields. An admin must approve.</p>
        <div>
          <Label className="text-xs">Semantic model</Label>
          <Select value={modelId} onValueChange={setModelId}>
            <SelectTrigger data-testid="unmask-model-select"><SelectValue placeholder="Select a model…" /></SelectTrigger>
            <SelectContent>{models.map(m => <SelectItem key={m.model_id} value={m.model_id}>{m.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {modelId && (
          <div>
            <Label className="text-xs">PII fields</Label>
            {piiFields.length === 0 ? (
              <p className="text-xs text-muted-foreground mt-1">No PII fields marked on this model.</p>
            ) : (
              <div className="flex flex-wrap gap-2 mt-1">
                {piiFields.map(f => (
                  <button key={f} type="button" onClick={() => toggle(f)} data-testid={`unmask-field-${f}`}
                    className={`px-2.5 py-1 rounded-lg text-xs border transition-colors ${selected.includes(f) ? 'border-primary bg-primary/10 text-primary' : 'border-border/50 hover:border-primary/40'}`}>{f}</button>
                ))}
              </div>
            )}
          </div>
        )}
        <div>
          <Label className="text-xs">Reason (optional)</Label>
          <Textarea value={reason} onChange={e => setReason(e.target.value)} placeholder="Why do you need unmasked access?" className="mt-1" data-testid="unmask-reason" />
        </div>
        <Button onClick={submit} disabled={submitting || !modelId || selected.length === 0} className="gap-2" data-testid="unmask-submit">
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldAlert className="w-4 h-4" />} Request access
        </Button>
      </div>

      <div className="max-w-2xl">
        <h3 className="text-sm font-semibold mb-2">My requests</h3>
        {mine.length === 0 ? <p className="text-xs text-muted-foreground">No requests yet.</p> : (
          <div className="space-y-2">
            {mine.map(r => (
              <div key={r.request_id} className="glass-card rounded-xl p-3 flex items-center justify-between text-sm">
                <div className="flex flex-wrap gap-1 items-center">
                  {(r.fields || []).map(f => <Badge key={f} variant="outline" className="text-[10px]">{f}</Badge>)}
                  {r.status === 'approved' && r.expires_at && <span className="text-[10px] text-muted-foreground ml-1">until {fmtTs(r.expires_at)}</span>}
                </div>
                <Badge variant="outline" className={r.status === 'approved' ? 'text-emerald-400 border-emerald-500/30' : r.status === 'denied' ? 'text-rose-400 border-rose-500/30' : 'text-amber-400 border-amber-500/30'}>{r.status}</Badge>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ConsistencySweep() {
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  useEffect(() => {
    (async () => {
      try { const m = await api.getSemanticModels(); setModels(Array.isArray(m) ? m : []); }
      catch (e) { toast.error(e.message || 'Could not load models'); }
      finally { setLoading(false); }
    })();
  }, []);
  const activeModel = models.find((m) => m.model_id === active);
  if (loading) return <div className="py-10 flex justify-center items-center text-sm text-muted-foreground"><Loader2 className="w-4 h-4 mr-2 animate-spin" />Loading models…</div>;
  return (
    <div className="space-y-2" data-testid="pii-consistency-models">
      <p className="text-sm text-muted-foreground mb-3">Scan a model for columns whose PII / category classification is inconsistent across its tables (half-flagged), and unify them in one click.</p>
      {models.length === 0 && <p className="text-sm text-muted-foreground">No semantic models.</p>}
      {models.map((m) => (
        <div key={m.model_id} className="glass-card rounded-xl p-4 flex items-center justify-between gap-3" data-testid={`pii-consistency-model-${m.model_id}`}>
          <div className="min-w-0">
            <div className="text-sm font-medium truncate">{m.name}</div>
            {m.description && <div className="text-xs text-muted-foreground truncate">{m.description}</div>}
          </div>
          <Button variant="outline" size="sm" className="rounded-lg text-xs h-7 gap-1.5 shrink-0" onClick={() => setActive(m.model_id)} data-testid={`pii-consistency-open-${m.model_id}`}>
            <ShieldAlert className="w-3.5 h-3.5 text-amber-500" /> Fix Inconsistencies
          </Button>
        </div>
      ))}
      <PiiConsistencyDialog modelId={active} modelName={activeModel?.name} open={!!active} onOpenChange={(v) => { if (!v) setActive(null); }} />
    </div>
  );
}

export default function PiiCompliancePage() {
  const { user } = useAuth();
  const isAdmin = ADMIN_ROLES.includes(user?.role);
  const [tab, setTab] = useState(isAdmin ? 'report' : 'request');

  const tabs = isAdmin
    ? [['report', 'Access Report'], ['approvals', 'Unmask Requests'], ['request', 'Request Access'], ['consistency', 'Fix Inconsistencies']]
    : [['request', 'Request Access']];

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="pii-compliance-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-rose-500/10 flex items-center justify-center"><ShieldAlert className="w-5 h-5 text-rose-400" /></div>
        <div>
          <h1 className="text-2xl font-semibold">PII Compliance</h1>
          <p className="text-sm text-muted-foreground">PII access reporting & temporary unmask requests.</p>
        </div>
      </div>

      <div className="flex gap-1 mb-6 border-b border-border/40">
        {tabs.map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)} data-testid={`pii-tab-${key}`}
            className={`px-4 py-2 text-sm border-b-2 -mb-px transition-colors ${tab === key ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>{label}</button>
        ))}
      </div>

      {tab === 'report' && isAdmin && <AccessReport />}
      {tab === 'approvals' && isAdmin && <Approvals />}
      {tab === 'request' && <RequestAccess />}
      {tab === 'consistency' && isAdmin && <ConsistencySweep />}
    </div>
  );
}
